import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Button, Select, Form, Input, InputNumber, Modal, DatePicker, Tag, Tooltip, message } from 'antd';
import { PlusOutlined, DownloadOutlined, EditOutlined, DeleteOutlined, CheckOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import dayjs from 'dayjs';

const billTypes = [
  { value: 'electricity', label: 'Electricity' },
  { value: 'gas', label: 'Gas' },
  { value: 'grocery', label: 'Grocery' },
  { value: 'vendor', label: 'Vendor Payment' },
  { value: 'water', label: 'Water' },
  { value: 'internet', label: 'Internet' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'other', label: 'Other' },
];

const BillsManagement = () => {
  const [bills, setBills] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [filters, setFilters] = useState({ hostelId: '', billType: '', status: '', startDate: '', endDate: '' });
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [markPaidForm] = Form.useForm();

  useEffect(() => {
    api.get('/hostels').then((res) => setHostels(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchBills();
  }, [filters]);

  const fetchBills = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.hostelId) params.hostelId = filters.hostelId;
      if (filters.billType) params.billType = filters.billType;
      if (filters.status) params.status = filters.status;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      const response = await api.get('/bills', { params });
      setBills(response.data);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to load bills');
    } finally {
      setLoading(false);
    }
  };

  const canEdit = user?.role === 'owner' || (user?.role === 'employee' && user?.employeeType === 'accountant');

  const handleAddBill = () => {
    setSelectedBill(null);
    form.resetFields();
    setShowModal(true);
  };

  const handleEditBill = (bill) => {
    setSelectedBill(bill);
    form.setFieldsValue({
      hostel: bill.hostel?._id || '',
      billType: bill.billType,
      vendorName: bill.vendorName,
      amount: bill.amount,
      dueDate: bill.dueDate ? dayjs(bill.dueDate) : null,
      description: bill.description,
      invoiceNumber: bill.invoiceNumber,
      paymentDate: bill.paymentDate ? dayjs(bill.paymentDate) : null,
      paymentMethod: bill.paymentMethod || 'cash',
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        hostel: values.hostel || null,
        billType: values.billType,
        vendorName: values.vendorName,
        amount: values.amount,
        dueDate: values.dueDate?.format('YYYY-MM-DD'),
        description: values.description,
        invoiceNumber: values.invoiceNumber,
      };
      if (selectedBill) {
        await api.patch(`/bills/${selectedBill._id}`, { ...payload, paymentDate: values.paymentDate?.format('YYYY-MM-DD'), paymentMethod: values.paymentMethod });
        message.success('Bill updated');
      } else {
        await api.post('/bills', payload);
        message.success('Bill created');
      }
      form.resetFields();
      setShowModal(false);
      setSelectedBill(null);
      fetchBills();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || 'Failed');
    }
  };

  const handleMarkPaid = (bill) => {
    setSelectedBill(bill);
    markPaidForm.setFieldsValue({ paymentDate: dayjs(), paymentMethod: 'cash' });
    setShowMarkPaidModal(true);
  };

  const handleMarkPaidSubmit = async () => {
    try {
      const values = await markPaidForm.validateFields();
      await api.patch(`/bills/${selectedBill._id}/mark-paid`, {
        paymentDate: values.paymentDate.format('YYYY-MM-DD'),
        paymentMethod: values.paymentMethod,
      });
      message.success('Bill marked as paid');
      markPaidForm.resetFields();
      setShowMarkPaidModal(false);
      setSelectedBill(null);
      fetchBills();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || 'Failed');
    }
  };

  const handleDelete = (billId) => {
    Modal.confirm({
      title: 'Delete this bill?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/bills/${billId}`);
          message.success('Bill deleted');
          fetchBills();
        } catch (error) {
          message.error(error.response?.data?.message || 'Delete failed');
        }
      },
    });
  };

  const handleExport = async () => {
    try {
      const params = { ...filters };
      const response = await api.get('/bills/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      let filename = 'bills';
      if (filters.billType) filename += `-${filters.billType}`;
      if (filters.status) filename += `-${filters.status}`;
      link.setAttribute('download', `${filename}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Export downloaded');
    } catch (error) {
      message.error(error.response?.data?.message || 'Export failed');
    }
  };

  const totalBills = bills.reduce((sum, b) => sum + b.amount, 0);
  const totalPending = bills.filter((b) => b.status === 'pending' || b.status === 'overdue').reduce((sum, b) => sum + b.amount, 0);
  const totalPaid = bills.filter((b) => b.status === 'paid').reduce((sum, b) => sum + b.amount, 0);

  const columns = [
    { title: 'Date', dataIndex: 'createdAt', key: 'date', render: (d) => dayjs(d).format('L') },
    { title: 'Type', dataIndex: 'billType', key: 'type', render: (t) => billTypes.find((bt) => bt.value === t)?.label || t },
    { title: 'Vendor', dataIndex: 'vendorName', key: 'vendor' },
    { title: 'Hostel', dataIndex: ['hostel', 'name'], key: 'hostel', render: (t) => t || 'Shared/Company-wide' },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (v) => `$${v.toFixed(2)}` },
    { title: 'Due Date', dataIndex: 'dueDate', key: 'dueDate', render: (d) => dayjs(d).format('L') },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={s === 'paid' ? 'green' : s === 'overdue' ? 'red' : 'orange'}>{s}</Tag>,
    },
    { title: 'Payment Date', dataIndex: 'paymentDate', key: 'paymentDate', render: (d) => (d ? dayjs(d).format('L') : '-') },
    ...(canEdit
      ? [
          {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {record.status !== 'paid' && (
                  <Tooltip title="Mark Paid">
                    <Button type="link" size="small" icon={<CheckOutlined />} onClick={() => handleMarkPaid(record)} />
                  </Tooltip>
                )}
                <Tooltip title="Edit">
                  <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditBill(record)} />
                </Tooltip>
                <Tooltip title="Delete">
                  <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} />
                </Tooltip>
              </div>
            ),
          },
        ]
      : []),
  ];

  if (loading && bills.length === 0) return null;

  return (
    <>
      <Card
        title={<span style={{ fontSize: 20, fontWeight: 600 }}>Bills Management</span>}
        extra={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>Export CSV</Button>
            {canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={handleAddBill}>Add Bill</Button>}
          </div>
        }
      >
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card><Statistic title="Total Bills" value={totalBills} precision={2} prefix="$" /></Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card><Statistic title="Pending/Overdue" value={totalPending} precision={2} prefix="$" valueStyle={{ color: '#ff4d4f' }} /></Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card><Statistic title="Paid" value={totalPaid} precision={2} prefix="$" valueStyle={{ color: '#52c41a' }} /></Card>
          </Col>
        </Row>

        <Card title="Filters" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <label>Hostel</label>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All Hostels"
                value={filters.hostelId || undefined}
                onChange={(v) => setFilters({ ...filters, hostelId: v || '' })}
                options={[{ value: '', label: 'All Hostels' }, { value: 'shared', label: 'Shared/Company-wide' }, ...hostels.map((h) => ({ value: h._id, label: h.name }))]}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label>Bill Type</label>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All Types"
                value={filters.billType || undefined}
                onChange={(v) => setFilters({ ...filters, billType: v || '' })}
                options={[{ value: '', label: 'All Types' }, ...billTypes]}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label>Status</label>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All Status"
                value={filters.status || undefined}
                onChange={(v) => setFilters({ ...filters, status: v || '' })}
                options={[{ value: '', label: 'All Status' }, { value: 'pending', label: 'Pending' }, { value: 'paid', label: 'Paid' }, { value: 'overdue', label: 'Overdue' }]}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label>Start Date</label>
              <Input type="date" value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} style={{ marginTop: 4 }} />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label>End Date</label>
              <Input type="date" value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} style={{ marginTop: 4 }} />
            </Col>
          </Row>
        </Card>

        <Card title="Bills">
          <Table rowKey="_id" columns={columns} dataSource={bills} loading={loading} pagination={{ pageSize: 10, showSizeChanger: true }} locale={{ emptyText: 'No bills found.' }} scroll={{ x: 'max-content' }} />
        </Card>
      </Card>

      <Modal
        title={selectedBill ? 'Edit Bill' : 'Add Bill'}
        open={showModal}
        onCancel={() => { setShowModal(false); setSelectedBill(null); form.resetFields(); }}
        onOk={handleSubmit}
        destroyOnClose
        okText={selectedBill ? 'Update' : 'Create'}
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="hostel" label="Hostel">
            <Select placeholder="Shared/Company-wide" options={[{ value: '', label: 'Shared/Company-wide' }, ...hostels.map((h) => ({ value: h._id, label: h.name }))]} />
          </Form.Item>
          <Form.Item name="billType" label="Bill Type" rules={[{ required: true }]}>
            <Select placeholder="Select type" options={billTypes} />
          </Form.Item>
          <Form.Item name="vendorName" label="Vendor Name" rules={[{ required: true }]}>
            <Input placeholder="Vendor name" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="amount" label="Amount" rules={[{ required: true }]}>
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dueDate" label="Due Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="invoiceNumber" label="Invoice Number">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Optional" />
          </Form.Item>
          {selectedBill && (
            <>
              <Form.Item name="paymentDate" label="Payment Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="paymentMethod" label="Payment Method">
                <Select options={[{ value: 'cash', label: 'Cash' }, { value: 'card', label: 'Card' }, { value: 'online', label: 'Online' }, { value: 'cheque', label: 'Cheque' }, { value: 'other', label: 'Other' }]} />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Modal title="Mark as Paid" open={showMarkPaidModal} onCancel={() => { setShowMarkPaidModal(false); setSelectedBill(null); markPaidForm.resetFields(); }} onOk={handleMarkPaidSubmit} destroyOnClose okText="Mark Paid">
        {selectedBill && (
          <Form form={markPaidForm} layout="vertical">
            <Form.Item name="paymentDate" label="Payment Date" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="paymentMethod" label="Payment Method" rules={[{ required: true }]}>
              <Select options={[{ value: 'cash', label: 'Cash' }, { value: 'card', label: 'Card' }, { value: 'online', label: 'Online' }, { value: 'cheque', label: 'Cheque' }, { value: 'other', label: 'Other' }]} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default BillsManagement;
